import { Checkbox, HelpText, Label, Tag, TextField } from '@lego/connect-components-react';
import { useTranslation } from 'react-i18next';
import type { FormFieldState, FormRuleActionState } from '../../types/types';
import { createUniqueId } from '../../utils/print';
import { LabeledField, NativeInput } from './formVariableUtils';

type VariableActionType = 'hide' | 'readonly' | 'set_value' | 'counter';

const ACTION_TYPES: VariableActionType[] = ['hide', 'readonly', 'set_value', 'counter'];

const EXCLUSIONS: Record<VariableActionType, VariableActionType[]> = {
  hide: ['readonly'],
  readonly: ['hide'],
  set_value: ['counter'],
  counter: ['set_value'],
};

const ACTION_LABEL_KEYS: Record<VariableActionType, string> = {
  hide: 'formPage.variableEditor.actionTypes.hide',
  readonly: 'formPage.variableEditor.actionTypes.readonly',
  set_value: 'formPage.variableEditor.actionTypes.setValue',
  counter: 'formPage.variableEditor.actionTypes.counter',
};

const NATIVE_TYPE_MAP: Record<string, string> = {
  Double: 'number',
  Currency: 'number',
  Date: 'date',
  CurrentDate: 'date',
  Time: 'time',
};

// ─── SetValueConfig ───────────────────────────────────────────────────────────

const SetValueConfig = ({
  config,
  selectedField,
  onUpdate,
}: {
  config: Record<string, unknown>;
  selectedField?: FormFieldState | null;
  onUpdate: (patch: Record<string, unknown>) => void;
}) => {
  const { t } = useTranslation();
  const fieldType = selectedField?.type ?? 'String';
  const nativeType = NATIVE_TYPE_MAP[fieldType];
  const currentValue = typeof config.value === 'string' ? config.value : '';

  return (
    <div className="mt-200">
      <div className="flex items-center gap-75 mb-75">
        <Label size="small">{t('formPage.variableEditor.value')}</Label>
        {selectedField && (
          <Tag size="small" variant="neutral">
            {fieldType}
          </Tag>
        )}
      </div>
      {nativeType ? (
        <NativeInput
          type={nativeType}
          value={currentValue}
          onChange={(v) => onUpdate({ value: v })}
        />
      ) : (
        <TextField
          size="medium"
          placeholder={t('formPage.variableEditor.value')}
          value={currentValue}
          onChange={(_e, value) => onUpdate({ value })}
        />
      )}
    </div>
  );
};

// ─── CounterConfig ────────────────────────────────────────────────────────────

type CounterField = {
  key: string;
  labelKey: string;
  hintKey?: string;
  min?: number;
  optional?: boolean;
  type: 'number' | 'text';
};

const COUNTER_FIELDS: CounterField[] = [
  { key: 'start', labelKey: 'counterStart', type: 'number' },
  { key: 'step', labelKey: 'counterStep', type: 'number', min: 1 },
  { key: 'max', labelKey: 'counterMax', type: 'number', min: 1 },
  {
    key: 'length',
    labelKey: 'counterLength',
    hintKey: 'counterLengthHint',
    type: 'number',
    min: 0,
    optional: true,
  },
  { key: 'prefix', labelKey: 'counterPrefix', type: 'text' },
  { key: 'suffix', labelKey: 'counterSuffix', type: 'text' },
];

const CounterConfig = ({
  config,
  onUpdate,
}: {
  config: Record<string, unknown>;
  onUpdate: (patch: Record<string, unknown>) => void;
}) => {
  const { t } = useTranslation();

  const renderField = (f: CounterField) => {
    const label = t(`formPage.variableEditor.${f.labelKey}`);
    const hint = f.hintKey ? t(`formPage.variableEditor.${f.hintKey}`) : undefined;

    if (f.type === 'text') {
      return (
        <LabeledField key={f.key} label={label} hint={hint}>
          <TextField
            size="medium"
            value={typeof config[f.key] === 'string' ? (config[f.key] as string) : ''}
            onChange={(_e, v) => onUpdate({ [f.key]: v })}
          />
        </LabeledField>
      );
    }

    return (
      <LabeledField key={f.key} label={label} hint={hint}>
        <NativeInput
          type="number"
          min={f.min}
          value={typeof config[f.key] === 'number' ? String(config[f.key]) : ''}
          onChange={(raw) => {
            if (raw === '') {
              if (f.optional) onUpdate({ [f.key]: undefined });
              return;
            }
            const n = Number(raw);
            if (Number.isFinite(n)) onUpdate({ [f.key]: n });
          }}
        />
      </LabeledField>
    );
  };

  return (
    <div className="mt-200 rounded-100 border border-core-slate-100 bg-core-slate-50 p-200">
      <p className="text-body-xs font-semibold text-core-slate-700 mb-150">
        {t('formPage.variableEditor.counterConfig')}
      </p>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-200">
        {COUNTER_FIELDS.map(renderField)}
      </div>
      <div className="mt-200">
        <Checkbox
          label={t('formPage.variableEditor.counterRollover')}
          checked={config.rollover !== false}
          onChange={(_e, checked) => onUpdate({ rollover: checked })}
        />
      </div>
      <HelpText size="small">{t('formPage.variableEditor.counterHelp')}</HelpText>
    </div>
  );
};

// ─── VariableActionsPanel ─────────────────────────────────────────────────────

export const VariableActionsPanel = ({
  actions,
  onChange,
  isFieldRequired,
  selectedField,
}: {
  actions: FormRuleActionState[];
  onChange: (next: FormRuleActionState[]) => void;
  isFieldRequired?: boolean;
  selectedField?: FormFieldState | null;
}) => {
  const { t } = useTranslation();

  const byType = new Map<string, FormRuleActionState>();
  for (const a of actions) byType.set(a.type, a);

  const getConfig = (type: VariableActionType) =>
    (byType.get(type)?.config ?? {}) as Record<string, unknown>;

  const setEnabled = (type: VariableActionType, enabled: boolean) => {
    const next = new Map(byType);
    if (enabled) {
      for (const c of EXCLUSIONS[type]) next.delete(c);
      if (!next.has(type)) next.set(type, { id: createUniqueId(), type, config: {} });
    } else {
      next.delete(type);
    }
    onChange(Array.from(next.values()));
  };

  const updateConfig = (type: VariableActionType, patch: Record<string, unknown>) => {
    const existing = byType.get(type);
    if (!existing) return;
    onChange(
      actions.map((a) =>
        a.id === existing.id ? { ...a, config: { ...(a.config ?? {}), ...patch } } : a
      )
    );
  };

  return (
    <div>
      <div className="flex flex-wrap gap-150 text-body-xs [&_*]:!text-body-xs">
        {ACTION_TYPES.map((type) => {
          const checked = byType.has(type);
          const disabled =
            (type === 'hide' && !!isFieldRequired) ||
            (!checked && EXCLUSIONS[type].some((c) => byType.has(c)));
          return (
            <Checkbox
              key={type}
              label={t(ACTION_LABEL_KEYS[type])}
              checked={checked}
              disabled={disabled}
              onChange={(_e, isChecked) => setEnabled(type, isChecked)}
            />
          );
        })}
      </div>
      {byType.has('set_value') && (
        <SetValueConfig
          config={getConfig('set_value')}
          selectedField={selectedField}
          onUpdate={(patch) => updateConfig('set_value', patch)}
        />
      )}
      {byType.has('counter') && (
        <CounterConfig
          config={getConfig('counter')}
          onUpdate={(patch) => updateConfig('counter', patch)}
        />
      )}
    </div>
  );
};
